import AnimationController from '../../../assets/js/controllers/animation';
import ResizeController from '../../../assets/js/controllers/resize';

import Video from '../../../components/video/script';

import VideoEvent from '../../../assets/js/constants/video-event';

import { getRootOffsetBounds } from '../../../assets/js/helpers';

export default class VideoPlayer {

	static selector = 'video-player';

	// class variables

	config = {
		wistiaId: null
	};

	components = {
		video: null,
		wistia: null
	};

	controllers = {
		animation: null,
		resize: null
	};

	coords = {
		container: null,
		progressBar: null,
	};

	el = {};

	flags = {
		isLoaded: false,
		isMouseOver: false,
		isMuted: false,
		isPlaying: false,
		isToggleVisible: false,
	};

	promises = {
		videoPlay: null
	};

	// constructor

	constructor(container) {
		this.el = {
			container,
			controls: {
				mute: container.querySelector(`.${this.constructor.selector}__control-progress-mute`),
				progressWrapper: container.querySelector(`.${this.constructor.selector}__control-progress`),
				progressBar: container.querySelector(`.${this.constructor.selector}__control-progress-bar`),
				progressBarProgress: container.querySelector(`.${this.constructor.selector}__control-progress-bar-progress`)
			},
			video: container.querySelector('video'),
			videoWistia: container.querySelector(`.${this.constructor.selector}__wistia`),
			videoWistiaEmbed: container.querySelector(`.${this.constructor.selector}__wistia .wistia_embed`)
		};
		container.instance = this;
		this.config.wistiaId = this.el.container.dataset.wistiaId;

		this.initialize();
	}

	// initialize

	initialize() {
		this.#addEventListeners();
	}

	// event listeners

	#addEventListeners = () => {
		this.el.container.addEventListener('click', this.#onClick);
		this.el.container.addEventListener('mouseenter', this.#onMouseEnter);
		this.el.container.addEventListener('mouseleave', this.#onMouseLeave);

		this.el.container.addEventListener(VideoEvent.RELOAD, this.#onReload);
		this.el.container.addEventListener(VideoEvent.UNLOAD, this.#onUnload);

		if (!this.config.wistiaId) {

			const willMute = this.el.video.hasAttribute('muted');
			const willPlay = this.el.container.hasAttribute('data-video-player-autoplay');

			this.el.controls.mute.addEventListener('click', this.#onMuteClick);
			this.el.controls.progressBar.addEventListener('click', this.#onProgressBarClick);

			// video component

			this.components.video = new Video(this.el.container);

			this.controllers.animation = AnimationController.set({
				update: this.#onAnimationUpdate,
				render: this.#onAnimationRender
			});

			// auto play and auto mute

			if (willPlay) {
				this.videoPlay();
			}

			if (willMute) {
				this.videoMute();
			}
		}

		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});

		this.measure();
	}

	#removeEventListeners = () => {
		this.el.container.removeEventListener('click', this.#onClick);
		this.el.container.removeEventListener('mouseenter', this.#onMouseEnter);
		this.el.container.removeEventListener('mouseleave', this.#onMouseLeave);

		this.el.container.removeEventListener(VideoEvent.RELOAD, this.#onReload);
		this.el.container.removeEventListener(VideoEvent.UNLOAD, this.#onUnload);

		if (this.components.video) {
			this.components.video.destroy();
		}

		AnimationController.clear(this.controllers.animation);
		ResizeController.clear(this.controllers.resize);

		this.controllers.animation = null;
		this.controllers.resize = null;
	}

	// listener methods

	#onClick = (e) => {
		if ((this.config.wistiaId && !this.el.videoWistiaEmbed.contains(e.target))
			|| (this.components.video && !this.el.controls.progressWrapper.contains(e.target))) {
			this.toggleVideoPlay();
		}
	}

	#onMouseEnter = () => {
		this.flags.isMouseOver = true;

	}

	#onMouseLeave = () => {
		this.flags.isMouseOver = false;
	}

	#onReload = () => {
		this.el.container.classList.remove('is-static');
	}

	#onUnload = () => {
		this.el.container.classList.add('is-static');
	}

	#onMuteClick = () => {
		this.toggleVideoMute();
	}

	#onProgressBarClick = (e) => {
		const ratio = (e.pageX - this.coords.progressBar.left) / this.coords.progressBar.width;
		const seconds = this.components.video.duration * ratio;
		this.videoProgressSeek(seconds);
	}

	#onVideoPause = () => {
		this.videoPauseRender();
	}

	#onVideoPlay = () => {
		this.videoPlayRender();
	}

	// controller methods

	#onAnimationUpdate = () => this.components.video.isPlaying;

	#onAnimationRender = () => {
		const progressRatio = this.components.video.currentTime / this.components.video.duration;
		this.videoProgressRender(progressRatio);
	}

	#onResizeUpdate = () => this.measureResize();

	#onResizeRender = () => {
		if (this.isPlaying) {
			this.videoReload();
		}
	}

	//

	#onWistiaBufferComplete = () => {
		this.components.wistia.unbind('timechange', this.#onWistiaBufferComplete);
		this.el.container.classList.add('is-video-active');
	}

	//

	measure() {
		this.measureResize();
	}

	measureResize() {
		this.coords.container = getRootOffsetBounds(this.el.container);
		this.coords.progressBar = getRootOffsetBounds(this.el.controls.progressBar);

		return ResizeController.isPlatformChange;
	}

	// video play status

	toggleVideoPlay() {
		if (this.isPlaying) {
			this.videoPause();
		} else {
			this.videoPlay();
		}
	}

	videoPause() {
		this.videoPauseRender();

		if (this.components.wistia) {
			this.components.wistia.pause();
		} else if (this.components.video) {
			// this.components.video.pause();
		}

		this.el.container.dispatchEvent(new CustomEvent(VideoEvent.PAUSE));
	}

	videoPlay() {
		if (this.config.wistiaId) {

			// play wistia video
			if (!this.components.wistia) {
				this.components.wistia = Wistia.api(this.config.wistiaId);

				this.components.wistia.bind('play', () => {
					this.videoPlayRender();
				});
				this.components.wistia.bind('pause', () => {
					this.videoPauseRender();
				});
			}

			this.components.wistia.bind('timechange', this.#onWistiaBufferComplete);
			this.components.wistia.play();

		} else {
			this.videoPlayRender();
			// this.components.video.play();
		}

		this.el.container.dispatchEvent(new CustomEvent(VideoEvent.PLAY));
	}

	videoPauseRender() {
		if (this.components.wistia) {
			this.el.container.classList.remove('is-video-playing');
			this.el.container.dispatchEvent(new CustomEvent(VideoEvent.PAUSING, { detail: { ...this.states } }));
		}
	}

	videoPlayRender() {
		if (this.components.wistia) {
			this.el.container.classList.add('is-video-playing');
			this.el.container.dispatchEvent(new CustomEvent(VideoEvent.PLAYING, { detail: { ...this.states } }));
		}
	}

	videoReload(willPlay = true) {
		if (this.components.video) {
			this.components.video.reload(willPlay);
			if (willPlay) {
				this.videoPlayRender();
			} else {
				this.videoPauseRender();
			}
		}
	}

	videoProgressSeek(seconds = 0) {
		this.components.video.seek(seconds);
		this.videoProgressRender(this.components.video.currentTime / this.components.video.duration);
	}

	videoProgressRender(ratio) {
		this.el.controls.progressBarProgress.style.transform = `scaleX(${ratio})`;
		this.el.container.dispatchEvent(new CustomEvent(VideoEvent.PROGRESS, { detail: { ...this.states } }));

		if (ratio === 1) {
			this.el.container.dispatchEvent(new CustomEvent(VideoEvent.PLAYING_COMPLETE, { detail: { ...this.states } }));
		}
	}

	// video sound

	toggleVideoMute() {
		if (!this.flags.isMuted) {
			this.videoMute();
		} else {
			this.videoUnmute();
		}
	}

	videoMute() {
		this.flags.isMuted = true;
		this.components.video.mute();
		this.el.container.classList.add('is-muted');
	}

	videoUnmute() {
		this.flags.isMuted = false;
		this.components.video.unmute();
		this.el.container.classList.remove('is-muted');
	}

	// getters

	get isPlaying() {
		if (this.components.wistia) {
			if (this.components.wistia.state() === 'playing') {
				return true;
			}
		} else if (this.components.video) {
			if (this.components.video.isPlaying) {
				return true;
			}
		}

		return false;
	}

	//

	destroy() {
		this.#removeEventListeners();
	}

}
